import { and } from '@lumapps/utils/function/predicate/and';
import { or } from '@lumapps/utils/function/predicate/or';
import { isUrl } from '@lumapps/utils/string/isUrl';
import { HTMLElementConvertOption } from '@lumapps/wrex-core/serialization/html/fromHTML/types';
import { matchAttribute, matchNode } from '@lumapps/wrex-core/serialization/html/fromHTML/utils';

import { ALIGNED_IMAGE_WRAPPER, IMAGE_ALIGNMENTS, IMAGE_GROUP, IMAGE_WIDTHS } from '../../../constants';
import { AlignedImageWrapperElement, ImageGroupElement } from '../../../types';
import { createAlignedImageWrapper } from '../../../utils/createAlignedImageWrapper';
import { createImageGroup } from '../../../utils/createImageGroup';

type ElementTypes = typeof IMAGE_GROUP;

/**
 * Element convert options.
 *
 * Warning: the order of these options does matter!
 */
export const ELEMENTS: { [Element in ElementTypes]: HTMLElementConvertOption<Element | typeof ALIGNED_IMAGE_WRAPPER> } =
    {
        [IMAGE_GROUP]: {
            test: matchNode({ tagName: 'img' }),
            testParent:
                // Match image with alignment in Microsoft Word
                or(
                    // Image aligned with text
                    and(
                        matchNode({ tagName: 'span' }),
                        or(matchAttribute('style', /float\s*:\s*right/), matchAttribute('style', /float\s*:\s*left/)),
                    ),
                    // Image aligned without text
                    and(
                        matchNode({ tagName: 'p' }),
                        or(
                            matchAttribute('style', /text-align\s*:\s*right/),
                            matchAttribute('style', /text-align\s*:\s*left/),
                            matchAttribute('style', /text-align\s*:\s*center/),
                        ),
                    ),
                ),
            type: IMAGE_GROUP,
            getAttributesFromParent: (
                node: any,
            ): Partial<AlignedImageWrapperElement | Partial<ImageGroupElement>> | undefined => {
                const style = node.properties?.style;
                if (style) {
                    if (style.includes('text-align: right') || style.includes('float: right')) {
                        return { alignment: IMAGE_ALIGNMENTS.right };
                    }
                    if (style.includes('text-align: left') || style.includes('float: left')) {
                        return { alignment: IMAGE_ALIGNMENTS.left };
                    }
                    if (style.includes('text-align: center')) {
                        return { width: IMAGE_WIDTHS.halfWidth };
                    }
                }

                return undefined;
            },
            transform: ({ properties: { alt, src }, attributes }: any) => {
                const isSrcUrl = isUrl(src);

                const imageGroup = createImageGroup({
                    // Do not upload external images until we make the uploadExternalImage call async
                    images: [isSrcUrl ? { alt, src } : { alt, tempSrc: src, src: '' }],
                    external: true,
                    isLoading: !isSrcUrl,
                    width: attributes?.width,
                });

                if (attributes?.alignment) {
                    return createAlignedImageWrapper(attributes, [imageGroup]);
                }

                return imageGroup;
            },
        },
    };
